'use client'

import { useState } from 'react'

import type { VariantProps } from 'cva'
import { cva } from 'cva'
import classNames from 'clsx'

import IconButton from '~/core/ui/IconButton'
import If from '~/core/ui/If'
import { CheckCircleIcon, CircleAlertIcon, InfoIcon, ShieldAlertIcon, XIcon } from 'lucide-react'

type Type = VariantProps<typeof alertClassNameBuilder>['type']

const icons = {
  success: (className: string) => <CheckCircleIcon className={className} />,
  error: (className: string) => <CircleAlertIcon className={className} />,
  warn: (className: string) => <ShieldAlertIcon className={className} />,
  info: (className: string) => <InfoIcon className={className} />,
  basic: (className: string) => <InfoIcon className={className} />,
}

const alertClassNameBuilder = getClassNameBuilder()
const alertIconClassNameBuilder = getIconClassNameBuilder()

const Alert: React.FCC<{
  type: Type
  useCloseButton?: boolean
  className?: string
}> & {
  Heading: typeof AlertHeading
} = ({ children, type, useCloseButton, className }) => {
  const [visible, setVisible] = useState(true)

  if (!visible) {
    return null
  }

  const alertClassName = alertClassNameBuilder({ type })
  const alertIconClassName = alertIconClassNameBuilder({ type })

  const Icon = icons[type ?? 'info'](alertIconClassName)

  return (
    <div role="alert" className={classNames(alertClassName, className)}>
      <div className="flex w-full justify-between">
        <div className="flex space-x-2">
          <div className="py-0.5">{Icon}</div>

          <div className="flex flex-col space-y-1">{children}</div>
        </div>

        <If condition={useCloseButton ?? false}>
          <div>
            <IconButton
              className="size-6 dark:hover:bg-transparent"
              onClick={() => setVisible(false)}
            >
              <XIcon className="h-5" />
            </IconButton>
          </div>
        </If>
      </div>
    </div>
  )
}

function AlertHeading({ children }: React.PropsWithChildren) {
  return (
    <h6>
      <span className="text-base font-medium">{children}</span>
    </h6>
  )
}

Alert.Heading = AlertHeading

function getClassNameBuilder() {
  return cva(
    [
      `animate-in fade-in relative flex items-center justify-between rounded-lg border p-4 text-sm text-gray-700`,
    ],
    {
      variants: {
        type: {
          success: `border-green-500/80 bg-green-500/5 text-green-800 dark:text-green-500`,
          info: `border-sky-800/50 bg-sky-500/5 text-sky-800 dark:border-sky-500/50 dark:text-sky-500`,
          error: `border-red-500/80 bg-red-500/5 text-red-500 dark:text-red-500`,
          warn: `border-yellow-500/80 bg-yellow-500/5 text-yellow-800 dark:text-yellow-500`,
          basic: ``,
        },
      },
      defaultVariants: {
        type: `info`,
      },
    },
  )
}

function getIconClassNameBuilder() {
  return cva([`size-5 rounded-full`], {
    variants: {
      type: {
        success: `text-green-800 dark:text-green-500`,
        info: `text-sky-800 dark:text-sky-500`,
        error: `text-red-500`,
        warn: `text-yellow-800 dark:text-yellow-500`,
        basic: `text-gray-800 dark:text-gray-500`,
      },
    },
    defaultVariants: {
      type: `info`,
    },
  })
}

export default Alert

export { Alert, AlertHeading }
